import { useState, useCallback, useRef, useEffect } from 'react';
import type { AttachmentItem, FileUploadConfig } from '../types/attachments';
import {
  createImagePreviewUrl,
  generateAttachmentId
} from '../utils/attachmentUtils';

const DEFAULT_MAX_FILES = 10;

export interface UseAttachmentsOptions {
  maxFiles?: number;
}

/**
 * Reusable hook for managing attachment state (Add Comment, Reply, Edit).
 * Caller must pass fileUploadConfig when file upload is enabled.
 */
export function useAttachments(options: UseAttachmentsOptions = {}) {
  const maxFiles = options.maxFiles ?? DEFAULT_MAX_FILES;
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const revokedRef = useRef<Set<string>>(new Set());

  const revokeIfNeeded = useCallback((item: AttachmentItem) => {
    if (item.previewUrl && !revokedRef.current.has(item.id)) {
      URL.revokeObjectURL(item.previewUrl);
      revokedRef.current.add(item.id);
    }
  }, []);

  const addFiles = useCallback(
    (
      files: File[] | FileList,
      config?: Pick<FileUploadConfig, 'maxSizePerFile'>
    ) => {
      const list = Array.from(files);
      const maxSize = config?.maxSizePerFile;
      setAttachments(prev => {
        const remaining = Math.max(0, maxFiles - prev.length);
        if (remaining === 0) return prev;
        const toAdd = list.slice(0, remaining).map(file => {
          const id = generateAttachmentId();
          const size = file.size;
          const rejected = maxSize != null && size > maxSize;
          const previewUrl = rejected ? undefined : createImagePreviewUrl(file);
          return {
            id,
            file,
            name: file.name,
            size,
            type: file.type,
            previewUrl
          } as AttachmentItem;
        });
        return [...prev, ...toAdd];
      });
    },
    [maxFiles]
  );

  const removeFile = useCallback(
    (id: string) => {
      setAttachments(prev => {
        const item = prev.find(a => a.id === id);
        if (item) {
          revokeIfNeeded(item);
          return prev.filter(a => a.id !== id);
        }
        return prev;
      });
    },
    [revokeIfNeeded]
  );

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(revokeIfNeeded);
      revokedRef.current = new Set();
      return [];
    });
  }, [revokeIfNeeded]);

  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach(item => {
        if (item.previewUrl && !revokedRef.current.has(item.id)) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  const getFiles = useCallback(
    () => attachments.map(a => a.file),
    [attachments]
  );

  return {
    attachments,
    addFiles,
    removeFile,
    clearAttachments,
    getFiles,
    attachmentCount: attachments.length
  } as const;
}
